/**
 * components/Chip/RemovableChip
 * An option-list value in Settings: the value, then a 15px round × button that
 * goes ink on hover.
 *
 * Removing a value does NOT delete it — it only leaves the picker, and every
 * record that already carries it keeps it (invariant 6). The Settings footer
 * says so; this component simply reports the intent.
 */
import { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { sans, mono, size as fontSize, numeric } from '../../theme/typography';
import { isWeb } from '../../lib/responsive';

export interface RemovableChipProps {
  label: string;
  onRemove: () => void;
  /** Currency codes are mono; every other option value is sans. */
  monospace?: boolean;
  disabled?: boolean;
}

export function RemovableChip({
  label,
  onRemove,
  monospace = false,
  disabled = false,
}: RemovableChipProps) {
  const [hovered, setHovered] = useState(false);
  const active = hovered && isWeb && !disabled;

  return (
    <View style={styles.chip}>
      <Text style={monospace ? styles.labelMono : styles.label}>{label}</Text>
      <Pressable
        onPress={disabled ? undefined : onRemove}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={`Remove ${label}`}
        style={[
          styles.remove,
          { backgroundColor: active ? neutral.ink : neutral.ruleLight },
        ]}
      >
        <Text style={[styles.removeGlyph, { color: active ? neutral.surface : neutral.grey600 }]}>
          ×
        </Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    // Asymmetric on purpose: the × needs less room on its side than the label.
    paddingVertical: 4,
    paddingLeft: 10,
    paddingRight: 4,
    borderWidth: 1,
    borderColor: neutral.border,
    borderRadius: radii.pill,
    backgroundColor: neutral.surface,
    alignSelf: 'flex-start',
  },
  label: { ...sans('400'), fontSize: fontSize.meta, color: neutral.ink },
  labelMono: { ...mono('500'), fontSize: fontSize.meta, ...numeric, color: neutral.ink },
  remove: {
    width: 15,
    height: 15,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radii.pill,
  },
  removeGlyph: { fontSize: 11, lineHeight: 12, ...sans('400') },
});

export default RemovableChip;
